import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Button } from '../Button/Button'
import { Input } from '../Input/Input'

/*
 * Bronson InlineEditable component.
 */
export const InlineEditable = ({ className, value, valid, submittedValue, onReset, onSubmit, ...otherProps }) => {
  /**
   * Manage active state to show editable mode.
   */
  const [isActive, setIsActive] = useState(false)
  /**
   * Set the {isActive} state to true and memoize the function.
   * @type {(function(): void)|*}
   */
  const activate = useCallback(() => {
    setIsActive(true)
  }, [])
  /**
   * Set the {isActive} state to false and memoize the function.
   * @type {(function(): void)|*}
   */
  const deactivate = useCallback(() => {
    setIsActive(false)
  }, [])
  /**
   * The InlineEditable class names.
   * @type {string}
   */
  const inlineEditableClassNameList = classNames('c-inline-editable', className).trim()

  /**
   * Edit Button
   * @type {JSX.Element}
   */
  const editButton = (
    <Button
      link
      simple
      small
      hiddenLabel
      className="c-inline-editable__btn"
      type="button"
      icon="semantic-edit"
      data-tr-component="Button"
      data-tr-function="Edit inline"
      onClick={activate}
    />
  )

  const submitButton = (
    <Button
      hiddenLabel
      className="c-inline-editable__submit"
      type="submit"
      icon="semantic-checkmark"
      data-tr-component="Button"
      data-tr-function="Edit inline"
      disabled={value === submittedValue || !valid}
      onClick={deactivate}
    />
  )

  const resetButton = (
    <Button
      secondary
      hiddenLabel
      type="reset"
      className="c-inline-editable__cancel"
      icon="semantic-crossed-out"
      data-tr-component="Button"
      data-tr-function="Edit inline"
      onClick={deactivate}
    />
  )

  return (
    <form
      className={inlineEditableClassNameList}
      onReset={onReset}
      onSubmit={onSubmit}
      aria-hidden={isActive ? 'false' : 'true'}
    >
      <div className="c-inline-editable__wrap">
        <Input
          modifierStates="c-inline-editable__input"
          addonClassName="c-inline-editable__input-addon c-input__addon--no-background"
          onClick={activate}
          size={1}
          divProps={{ 'data-inline-editable-size': value ?? 1 }}
          {...otherProps}
          value={value}
        />
        {editButton}
        {submitButton}
        {resetButton}
      </div>
    </form>
  )
}

InlineEditable.propTypes = {
  className: PropTypes.string,
  value: PropTypes.string,
  submittedValue: PropTypes.string,
  valid: PropTypes.bool,
  onReset: PropTypes.func,
  onSubmit: PropTypes.func,
  errorMessage: PropTypes.node, // Bronson template: 'bronson-error-message'.
}
